import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  formatDateToWeekdayString,
  formatTimeRange,
  formatTime,
  getSecondsLeft,
  getTimeColorClass,
  formatStatus,
  Toast,
  Modal,
  STATUS_BADGE_CLASS,
} from '@repo/common';
import OptionList from '../../AdminPayments/PendingPayments/OptionList';
import {
  cancelPayment,
  confirmBankTransfer,
  resendPaymentRequest,
  refundPayment,
} from '../../../../api/payment/payment.api';

import { ReactComponent as OptionIcon } from '@repo/common/assets/icons/20px/icon_20px_option_filled.svg';

const InvoiceRow = ({ item, index, isAdmin, $isMobile }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [showOptionList, setShowOptionList] = useState(false);
  const [anchorRect, setAnchorRect] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedBookingId, setSelectedBookingId] = useState(null);

  const [showSendSuccessToast, setShowSendSuccessToast] = useState(false);
  const [showCancelSuccessToast, setShowCancelSuccessToast] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [showRefundSuccessToast, setShowRefundSuccessToast] = useState(false);
  const [refundProcessing, setRefundProcessing] = useState(false);

  // 계좌이체 인보이스(Prana Club 한옥 웰니스 등): PG 없음. 이체 확인·수동 환불 확인 메뉴가 붙는다
  const isBankTransfer =
    String(item?.paymentProvider || '').toUpperCase() === 'BANK_TRANSFER';
  const isKrw = String(item?.currency || '').toUpperCase() === 'KRW';
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showConfirmSuccessToast, setShowConfirmSuccessToast] = useState(false);
  const [confirmProcessing, setConfirmProcessing] = useState(false);

  const isRefundable =
    isAdmin &&
    item?.paymentId != null &&
    (['CONFIRMED', 'ATTENDED', 'ABSENT'].includes(item?.status) ||
      // 계좌이체만: 유저가 환불을 요청해 REFUNDED 로 바뀐 뒤에도 어드민이 "환불 완료" 를 기록해야 한다
      (isBankTransfer &&
        item?.status === 'REFUNDED' &&
        item?.refundStatus !== 'COMPLETED'));

  const formattedAmount = item?.amount
    ? isKrw
      ? Math.round(Number(item.amount)).toLocaleString('en-US')
      : item.amount.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
    : isKrw
      ? '0'
      : '0.00';
  const amountSuffix = isKrw ? 'KRW' : '$';

  // 어드민 옵션 메뉴를 보여 줄 상태: 결제 대기(Pending) — 계좌이체는 만료(EXPIRED)된 건도 "늦은 입금 확인" 을 위해 연다
  const canOpenOptions =
    isAdmin &&
    (formatStatus(item?.status) === 'Pending' ||
      (isBankTransfer && item?.status === 'EXPIRED'));

  const handleConfirmBankTransfer = () => {
    setShowConfirmModal(true);
  };

  const handleConfirmBankTransferConfirm = async () => {
    if (confirmProcessing) return;
    setConfirmProcessing(true);
    try {
      await confirmBankTransfer({
        invoiceNumber: item?.invoiceNumber ?? searchParams.get('invoice'),
        paymentId: item?.paymentId,
      });
      setShowConfirmModal(false);
      setShowConfirmSuccessToast(false);
      setTimeout(() => setShowConfirmSuccessToast(true), 10);
      setTimeout(() => window.location.reload(), 1000);
    } catch (err) {
      console.error('Failed to confirm bank transfer:', err);
      // 서버 에러 응답은 { code, msg, data } 형식 — msg 를 먼저 본다
      alert(
        err?.response?.data?.msg ||
          err?.response?.data?.message ||
          `Could not confirm the transfer (HTTP ${err?.response?.status ?? '?'}). Please try again.`,
      );
    } finally {
      setConfirmProcessing(false);
    }
  };

  // 유저가 요청만 한 상태(예약은 REFUNDED, 결제 환불은 아직) — "Refunded" 로만 보이면 이미 끝난 것으로 오해한다
  const refundPending =
    item?.status === 'REFUNDED' &&
    ['REQUESTED', 'PROCESSING'].includes(String(item?.refundStatus || ''));

  const refundModalTitle = isBankTransfer
    ? 'Mark refund as completed?'
    : 'Refund this payment?';
  const refundModalText = isBankTransfer
    ? `No PG call is made for bank-transfer bookings. Confirm only after the organizer has sent ${formattedAmount} ${amountSuffix} back to the guest. The booking will be marked as refunded and the seat released. This action cannot be undone.`
    : `The full amount will be refunded to the original payment method via the PG, and the booking will be marked as refunded. This action cannot be undone.`;
  const refundButtonLabel = isBankTransfer ? 'Confirm refund' : 'Force refund';

  const handleSendPaymentRequest = async () => {
    try {
      await resendPaymentRequest([item.bookingId]);
      setShowSendSuccessToast(false);
      setTimeout(() => setShowSendSuccessToast(true), 10);
    } catch (err) {
      console.error('Failed to send payment request:', err);
    }
  };

  const handleCancelPayment = () => {
    setShowCancelModal(true);
  };

  const handleCancelPaymentConfirm = async () => {
    try {
      const ids = selectedBookingId ? [selectedBookingId] : [item.bookingId];
      await cancelPayment(ids);
      setShowCancelSuccessToast(false);
      setTimeout(() => setShowCancelSuccessToast(true), 10);
      setShowCancelModal(false);
      setTimeout(() => window.location.reload(), 1000);
    } catch (err) {
      console.error('Failed to cancel payment:', err);
    }
  };

  const handleRequestRefund = (e) => {
    e.stopPropagation();
    const invoiceNumber = item?.invoiceNumber ?? searchParams.get('invoice');
    navigate(`/mypage/${invoiceNumber}/refund`, {
      state: { bookingIds: [item.bookingId] },
    });
  };

  const handleRefundConfirm = async () => {
    if (refundProcessing) return;
    setRefundProcessing(true);
    try {
      await refundPayment({
        paymentId: item.paymentId,
        refundReason: 'Admin refund',
        adminOverrideReason: 'Admin manual refund',
      });
      setShowRefundModal(false);
      setShowRefundSuccessToast(false);
      setTimeout(() => setShowRefundSuccessToast(true), 10);
      setTimeout(() => window.location.reload(), 1000);
    } catch (err) {
      console.error('Failed to refund payment:', err);
      alert('환불 처리에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setRefundProcessing(false);
    }
  };

  const badgeLabel = formatStatus(item?.status);
  const badgeClass =
    STATUS_BADGE_CLASS[badgeLabel] ?? 'bg-grayscale-gray02 text-text-secondary';

  if ($isMobile) {
    const statusLabel = formatStatus(item?.status);
    return (
      <div className='flex flex-col gap-2.5 w-full py-[14px] border-b border-grayscale-gray01'>
        <div className='flex items-center justify-between gap-1 typo-b11Strong text-text-secondary'>
          <div>Item ID #{String(index + 1).padStart(2, '0')}</div>
          <span className='typo-b14Strong text-text-primary'>
            {formattedAmount} {amountSuffix}
          </span>
        </div>

        <div className='typo-b14Strong text-text-primary'>
          [{formatDateToWeekdayString(item?.date)} ∙{' '}
          {formatTimeRange(item?.startTime, item?.endTime)}] {item?.title}
        </div>

        <div className='flex items-center gap-2 flex-wrap'>
          <span
            className={`px-1 py-[1px] rounded-md typo-b11Strong ${badgeClass}`}
          >
            {statusLabel}
          </span>
          {statusLabel === 'Canceled' && (
            <span className='typo-b12 text-text-tertiary'>
              #{item.canceledSerialNumber}
            </span>
          )}
          {/* 참석 완료(ATTENDED/ABSENT)도 "Confirmed" 로 표시되지만 환불 요청 대상은 CONFIRMED 만이다 */}
          {!isAdmin && item?.status === 'CONFIRMED' && (
            <button
              className='typo-b12Strong text-text-tertiary underline'
              onClick={handleRequestRefund}
            >
              Request a refund
            </button>
          )}
          {refundPending && (
            <span className='typo-b11Strong text-text-accent'>Refund requested</span>
          )}
          {isRefundable && (
            <button
              className='typo-b11 text-text-placeholder underline'
              onClick={(e) => {
                e.stopPropagation();
                setShowRefundModal(true);
              }}
            >
              {refundButtonLabel}
            </button>
          )}
          {canOpenOptions && isBankTransfer && (
            <button
              className='typo-b11Strong text-green-deep03 underline'
              onClick={(e) => {
                e.stopPropagation();
                setShowConfirmModal(true);
              }}
            >
              {item?.status === 'EXPIRED' ? 'Confirm late transfer' : 'Confirm bank transfer'}
            </button>
          )}
        </div>

        {showRefundModal && (
          <Modal
            isOpen
            onClickLeftBtn={() => setShowRefundModal(false)}
            onClickRightBtn={handleRefundConfirm}
            title={refundModalTitle}
            subText={refundModalText}
            buttonSize='xl'
            leftButtonLabel='Keep'
            leftHideIcon
            rightButtonLabel={isBankTransfer ? 'Mark refunded' : 'Refund'}
            rightHideIcon
            type='warning'
          />
        )}
        {showConfirmModal && (
          <Modal
            isOpen
            onClickLeftBtn={() => setShowConfirmModal(false)}
            onClickRightBtn={handleConfirmBankTransferConfirm}
            title={'Confirm bank transfer?'}
            subText={`Confirm only after the organizer has verified the deposit of ${formattedAmount} ${amountSuffix}. The booking will be marked as confirmed and a confirmation email will be sent to the guest.`}
            buttonSize='xl'
            leftButtonLabel='Not yet'
            leftHideIcon
            rightButtonLabel='Confirm'
            rightHideIcon
            type='default'
          />
        )}
        {showConfirmSuccessToast && (
          <div className='fixed bottom-6 left-1/2 -translate-x-1/2 z-[1000]'>
            <Toast
              title={'Transfer confirmed. Booking confirmed.'}
              hierarchy='important'
              direction='up'
              customWidth={'320px'}
            />
          </div>
        )}
        {showRefundSuccessToast && (
          <div className='fixed bottom-6 left-1/2 -translate-x-1/2 z-[1000]'>
            <Toast
              title={'Refund processed.'}
              hierarchy='important'
              direction='up'
              customWidth={'232px'}
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className={`flex items-center h-[90px] relative z-10 typo-b14 text-text-secondary w-full border-b border-grayscale-gray02`}
    >
      <span className='flex justify-center items-center p-1 w-[120px] gap-1'>
        <span className='text-text-placeholder'>Item ID</span>{' '}
        <span>#{String(index + 1).padStart(2, '0')}</span>
      </span>

      <span className='flex px-2 py-1 flex-col items-center justify-center text-center gap-0.5 typo-b14Strong text-text-primary flex-1 w-full min-w-0'>
        <span className='line-clamp-2 w-full min-w-0'>
          [{formatDateToWeekdayString(item?.date)} ∙{' '}
          {formatTimeRange(item?.startTime, item?.endTime)}] {item?.title}
        </span>

        {!isAdmin && item?.status === 'CONFIRMED' && (
          <button
            className='typo-b11 text-text-placeholder underline'
            onClick={handleRequestRefund}
          >
            Request a refund
          </button>
        )}
        {refundPending && (
          <span className='typo-b11Strong text-text-accent'>Refund requested</span>
        )}
        {isRefundable && (
          <button
            className='typo-b11 text-text-placeholder underline hover:text-text-accent'
            onClick={(e) => {
              e.stopPropagation();
              setShowRefundModal(true);
            }}
          >
            {refundButtonLabel}
          </button>
        )}
        {isBankTransfer && (
          <span className='typo-b11 text-text-placeholder'>Bank transfer</span>
        )}
      </span>

      <span className='flex p-1 flex-col items-center justify-center w-[120px]'>
        {formatStatus(item?.status)}
        {formatStatus(item?.status) === 'Canceled' && (
          <span>#{item.canceledSerialNumber}</span>
        )}
        {item?.status === 'HOLD' && (
          <span
            className={getTimeColorClass(getSecondsLeft(item.holdExpiresAt))}
          >
            {formatTime(getSecondsLeft(item.holdExpiresAt))}
          </span>
        )}
        {item?.status === 'OFFERED' && (
          <span
            className={getTimeColorClass(getSecondsLeft(item.offerExpiresAt))}
          >
            {formatTime(getSecondsLeft(item.offerExpiresAt))}
          </span>
        )}
      </span>

      <span className='flex p-1 flex-col items-center justify-center gap-2.5 w-[100px]'>
        {formattedAmount} {amountSuffix}
      </span>

      {isAdmin && (
        <span className='flex px-0.5 flex-col items-center justify-center gap-2.5 min-w-[40px] max-w-[80px] w-full'>
          <button
            className={`text-icon-placeholder hover:text-icon-tertiary active:text-icon-tertiary ${
              canOpenOptions ? 'visible' : 'invisible'
            }`}
            onClick={(e) => {
              if (!canOpenOptions) return;
              e.stopPropagation();
              const rect = e.currentTarget.getBoundingClientRect();
              setAnchorRect(rect);
              setSelectedBookingId(null);
              setSelectedStatus(formatStatus(item?.status));
              setShowOptionList(true);
            }}
          >
            <OptionIcon className='text-icon-placeholder hover:text-icon-tertiary' />
          </button>
        </span>
      )}

      {showOptionList && (
        <OptionList
          anchorRect={anchorRect}
          onClose={() => setShowOptionList(false)}
          status={selectedStatus}
          paymentProvider={item?.paymentProvider}
          hasPayment={item?.paymentId != null}
          onSendPaymentRequest={handleSendPaymentRequest}
          onCanclePayment={handleCancelPayment}
          onConfirmBankTransfer={handleConfirmBankTransfer}
        />
      )}

      {showConfirmModal && (
        <Modal
          isOpen
          onClickLeftBtn={() => setShowConfirmModal(false)}
          onClickRightBtn={handleConfirmBankTransferConfirm}
          title={'Confirm bank transfer?'}
          subText={`Confirm only after the organizer has verified the deposit of ${formattedAmount} ${amountSuffix}.
The booking will be marked as confirmed and a confirmation email will be sent to the guest.`}
          buttonSize='xl'
          leftButtonLabel='Not yet'
          leftHideIcon={true}
          rightButtonLabel='Confirm'
          rightHideIcon={true}
          type='default'
        />
      )}

      {showConfirmSuccessToast && (
        <div className='fixed bottom-6 left-1/2 -translate-x-1/2 z-[1000]'>
          <Toast
            title={'Transfer confirmed. Booking confirmed.'}
            hierarchy='important'
            direction='up'
            customWidth={'320px'}
          />
        </div>
      )}

      {showSendSuccessToast && (
        <div className='fixed bottom-6 left-1/2 -translate-x-1/2 z-[1000]'>
          <Toast
            title={'Payment request email sent.'}
            hierarchy='important'
            direction='up'
            customWidth={'320px'}
          />
        </div>
      )}

      {showCancelSuccessToast && (
        <div className='fixed bottom-6 left-1/2 -translate-x-1/2 z-[1000]'>
          <Toast
            title={'Payment canceled.'}
            hierarchy='important'
            direction='up'
            customWidth={'232px'}
          />
        </div>
      )}

      {showCancelModal && (
        <Modal
          isOpen
          onClickLeftBtn={() => {
            setShowCancelModal(false);
          }}
          onClickRightBtn={handleCancelPaymentConfirm}
          title={'Cancel payment?'}
          subText={`This will cancel the payment and void
the invoice. Any associated bookings will be canceled. This action cannot be undone.`}
          buttonSize='xl'
          leftButtonLabel='Keep'
          leftHideIcon={true}
          rightButtonLabel='Cancel'
          rightHideIcon={true}
          type='warning'
        />
      )}

      {showRefundModal && (
        <Modal
          isOpen
          onClickLeftBtn={() => setShowRefundModal(false)}
          onClickRightBtn={handleRefundConfirm}
          title={refundModalTitle}
          subText={refundModalText}
          buttonSize='xl'
          leftButtonLabel='Keep'
          leftHideIcon={true}
          rightButtonLabel={isBankTransfer ? 'Mark refunded' : 'Refund'}
          rightHideIcon={true}
          type='warning'
        />
      )}

      {showRefundSuccessToast && (
        <div className='fixed bottom-6 left-1/2 -translate-x-1/2 z-[1000]'>
          <Toast
            title={'Refund processed.'}
            hierarchy='important'
            direction='up'
            customWidth={'232px'}
          />
        </div>
      )}
    </div>
  );
};

export default InvoiceRow;
